import { observable, runInAction } from 'mobx'

import { StorageKey, StorageStore } from '../storage'
import { action, normalizeCompanyColorScheme, NormalizedCompanyColorScheme } from '../helper'
import { Model } from '../general'

interface RootStore {
  storage: StorageStore
}

export interface WorkHours {
  startAt: string
  endAt: string
}

export interface ColorScheme {
  occupancy_colors?: Record<string, string>
  trigger_colors?: Record<string, string>
  schedule_colors?: Record<string, string>
}

export interface Data {
  id: number
  name?: string
  nr_of_locations?: number
  nr_of_workspaces?: number
  workhours_starttime?: string
  workhours_endtime?: string
  vitality_trigger?: boolean
  has_regular_sync?: boolean
  color_scheme?: ColorScheme
}

type NormalizedData = Data & {
  colorScheme: NormalizedCompanyColorScheme
}

class Company extends Model<NormalizedData, RootStore> {
  private pinned = observable.box<number | undefined>(undefined)

  constructor(parent: RootStore, data: Data) {
    super(parent, {
      ...data,
      colorScheme: normalizeCompanyColorScheme(data.color_scheme),
    })
    this.updatePinned()
  }

  get name() {
    return this.data.name || ''
  }

  get numberOfLocations() {
    return this.data.nr_of_locations || 0
  }

  get numberOfWorkspaces() {
    return this.data.nr_of_workspaces || 0
  }

  get searchText(): string {
    return `${this.name}`
  }

  get isPinned() {
    return !!this.pinned.get()
  }

  get pinnedDate() {
    const timestamp = this.pinned.get()
    return timestamp ? new Date(timestamp) : undefined
  }

  get workHours() {
    return {
      startAt: this.data.workhours_starttime || '0:00',
      endAt: this.data.workhours_endtime || '0:00',
    } as WorkHours
  }

  togglePinned = action(async () => {
    if (!this.id) throw new Error('Company id missing')
    if (this.isPinned) {
      await this.unpin()
    } else {
      await this.pin()
    }
  })

  get vitalityTrigger() {
    return this.data.vitality_trigger || false
  }

  get hasRegularSync() {
    return this.data.has_regular_sync || false
  }

  get configuredOccupancyColors(): Record<string, string> {
    return this.data.color_scheme?.occupancy_colors || {}
  }

  get colorScheme(): NormalizedCompanyColorScheme {
    return this.data.colorScheme
  }

  pin = async () => {
    const { storage } = this.store
    const now = Date.now()
    storage?.get(StorageKey.Companies, undefined, async (data) => {
      const pinned = JSON.parse(data || '{}')
      pinned[this.id] = now
      await storage?.set(StorageKey.Companies, JSON.stringify(pinned))
      runInAction(() => {
        this.pinned.set(now)
      })
    })
  }

  unpin = async () => {
    const { storage } = this.store
    storage?.get(StorageKey.Companies, undefined, async (data) => {
      const pinned = JSON.parse(data || '{}')
      delete pinned[this.id]
      await storage?.set(StorageKey.Companies, JSON.stringify(pinned))
      runInAction(() => {
        this.pinned.set(undefined)
      })
    })
  }

  private updatePinned = async () => {
    if (!this.id) return
    const { storage } = this.store
    storage?.get(StorageKey.Companies, undefined, async (data) => {
      const pinned = JSON.parse(data || '{}')
      runInAction(() => {
        this.pinned.set(pinned[this.id])
      })
    })
  }
}

export default Company
